<template>
  <div
    ref="root"
    data-feedback-ui
    data-test="feedback-screenshot-editor"
    class="fixed inset-0 z-dialog flex flex-col bg-theme-bg"
    role="dialog"
    aria-modal="true"
    tabindex="-1"
    :aria-label="t('feedback.annotate.dialog_title')"
  >
    <header
      class="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-theme-border px-4 py-3"
    >
      <div class="min-w-0">
        <h2 class="text-sm font-semibold text-theme-text">{{ t('feedback.annotate.dialog_title') }}</h2>
        <p class="mt-0.5 text-xs text-theme-text-secondary">{{ t('feedback.annotate.dialog_hint') }}</p>
      </div>
      <div class="flex shrink-0 items-center gap-2">
        <app-button
          size="sm"
          variant="ghost"
          tone="danger"
          :icon="mdiTrashCanOutline"
          :label="t('feedback.remove')"
          data-test="feedback-editor-remove"
          @click="$emit('remove')"
        />
        <app-button
          size="sm"
          tone="primary"
          :icon="mdiCheck"
          :label="t('feedback.annotate.done')"
          data-test="feedback-editor-done"
          @click="$emit('done')"
        />
      </div>
    </header>

    <div class="flex min-h-0 flex-1 flex-col overflow-auto px-4 py-3">
      <screenshot-annotator :shot-id="shotId" />
    </div>
  </div>
</template>

<script setup>
  /**
   * Full-screen screenshot editor. Opens by itself as soon as a shot is
   * captured, pasted or uploaded, and again from the form's Edit button —
   * redacting something on a thumbnail-sized canvas is guesswork, and a
   * redaction you cannot aim is worse than none.
   *
   * It owns no state: the shot lives on the draft and the annotator edits it
   * in place, so closing and reopening shows exactly what was left behind.
   */
  import { onBeforeUnmount, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { mdiCheck, mdiTrashCanOutline } from '@mdi/js';

  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import ScreenshotAnnotator from './ScreenshotAnnotator.vue';

  defineProps({
    shotId: { type: String, required: true },
  });
  const emit = defineEmits(['done', 'remove']);

  const { t } = useI18n();
  const root = ref(null);

  // Escape closes from anywhere, not only when focus happens to be inside the
  // dialog — the annotator's own keydown handler owns the arrow keys and
  // Delete, and never sees Escape.
  function onEscape(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      emit('done');
    }
  }

  onMounted(() => {
    root.value?.focus?.();
    window.addEventListener('keydown', onEscape);
  });

  onBeforeUnmount(() => window.removeEventListener('keydown', onEscape));
</script>
